import React, { useCallback } from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';

import FilterBarSelectInput from 'components/@common/FilterBar/FilterBarSelectInput';
import FilterBarSearchInput from 'components/@common/FilterBar/FilterBarSearchInput';

import CancelIcon from '@material-ui/icons/Cancel';
import { Organization, Maybe, Product, User } from 'types/schema-generated-types';
import { ClassroomFilterValue } from 'components/ClassroomsModule/types';

const useStyles = makeStyles((theme: Theme) => {
  return {
    reportControlsContainer: {
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
      paddingTop: theme.spacing() * 1.5,
      paddingBottom: theme.spacing() * 1.5,
      boxShadow: theme.shadows[2],
      background: 'white'
    },
    select: {
      width: '100%'
    },
    buttonContainer: {
      height: '100%'
    },
    label: {
      fontWeight: 600
    },
    iconButton: {
      width: 36,
      height: 36,
      minWidth: 'unset',
      padding: 'unset'
    },
    icon: {
      height: 18,
      width: 18
    }
  };
});

interface ClassesManagementFilterBarProps {
  values: any;
  handleChange: (
    eventOrPath: string | React.ChangeEvent<any>
  ) => void | ((eventOrTextValue: string | React.ChangeEvent<any>) => void);
  resetForm: VoidFunction;
  organizations: Maybe<Array<Organization>>;
  educators: Maybe<Array<User>>;
  products: Maybe<Array<Product>>;
}

const ClassesManagementFilterBar = ({
  values,
  handleChange,
  resetForm,
  organizations,
  products,
  educators
}: ClassesManagementFilterBarProps): React.ReactElement => {
  const classes = useStyles();
  const { t } = useTranslation('ClassesManagementFilterBar');

  const buildProductMenuOptions = useCallback(
    () =>
      // eslint-disable-next-line react/display-name
      products?.map(product => (
        <MenuItem key={`${product.id}-product-option`} value={product.code}>
          {product.name}
        </MenuItem>
      )),
    [products]
  );

  const buildEducatorMenuOptions = useCallback(
    () =>
      // eslint-disable-next-line react/display-name
      educators?.map(user => (
        <MenuItem key={`${user.id}-user-option`} value={user.id}>
          {user.firstName} {user.lastName}
        </MenuItem>
      )),
    [educators]
  );

  const buildClassroomMenuOptions = useCallback(
    () => [
      <MenuItem key="classroom-active" value={ClassroomFilterValue.ACTIVE}>
        Active Classes
      </MenuItem>,
      <MenuItem key="classroom-archived" value={ClassroomFilterValue.ARCHIVE}>
        Archived Classes
      </MenuItem>
    ],
    []
  );

  const buildOrganizationMenuOptions = useCallback(
    () =>
      // eslint-disable-next-line react/display-name
      organizations?.map(org => (
        <MenuItem key={`${org.type}-${org.id}-option`} value={org.id}>
          {org.name}
        </MenuItem>
      )),
    [organizations]
  );

  return (
    <div className={classes.reportControlsContainer}>
      <Grid container spacing={3}>
        <Grid xs={12} md={2} item>
          <FilterBarSelectInput
            formControlProps={{
              className: classes.select
            }}
            name="organization"
            placeholder={t('All Organizations')}
            value={values.organization}
            size="small"
            inputProps={{
              'aria-label': t('Organizations')
            }}
            onChange={handleChange}
          >
            {buildOrganizationMenuOptions()}
          </FilterBarSelectInput>
        </Grid>
        <Grid xs={12} md={2} item>
          <FilterBarSelectInput
            formControlProps={{
              className: classes.select
            }}
            placeholder={t('Active Classes')}
            name="classroomStatus"
            value={values.classroomStatus}
            size="small"
            inputProps={{
              'aria-label': t('Classes')
            }}
            onChange={handleChange}
          >
            {buildClassroomMenuOptions()}
          </FilterBarSelectInput>
        </Grid>
        <Grid xs={12} md={2} item>
          <FilterBarSelectInput
            formControlProps={{
              className: classes.select
            }}
            placeholder={t('All Educators')}
            value={values.educator}
            size="small"
            inputProps={{
              'aria-label': t('Educators')
            }}
            name="educator"
            onChange={handleChange}
          >
            {buildEducatorMenuOptions()}
          </FilterBarSelectInput>
        </Grid>
        <Grid xs={12} md={2} item>
          <FilterBarSelectInput
            inputProps={{
              'aria-label': t('Product Used')
            }}
            formControlProps={{
              className: classes.select
            }}
            onChange={handleChange}
            name="product"
            placeholder={t('All Products')}
            value={values.product}
          >
            {buildProductMenuOptions()}
          </FilterBarSelectInput>
        </Grid>
        <Grid xs={12} md={2} item>
          <FilterBarSearchInput
            inputProps={{
              'aria-label': t('Search')
            }}
            onChange={handleChange}
            name="search"
            placeholder={t('Search')}
            value={values.search}
          />
        </Grid>
        <Grid xs={12} md={2} item>
          <Grid container alignContent="center" className={classes.buttonContainer}>
            <Grid item>
              <Button
                variant="text"
                size="small"
                disableElevation
                onClick={resetForm}
                startIcon={<CancelIcon />}
              >
                Clear All Filters
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default ClassesManagementFilterBar;
